import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPen, 
  faArrowLeft, 
  faUser, 
  faEnvelope, 
  faIdBadge, 
  faCalendar,
  faToggleOn,
  faToggleOff,
  faKey,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { usuarioService } from '../../../services/usuarioService';
import { useAuth } from '../../../contexts/AuthContext';

const UsuarioView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsuario();
  }, [id]);

  const loadUsuario = async () => {
    try {
      setLoading(true);
      console.log('Carregando usuário com ID:', id);
      const response = await usuarioService.getById(id);
      console.log('Resposta do serviço:', response);
      setUsuario(response.data);
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!usuario) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Usuário não encontrado.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/seguranca/usuarios')}
            className="text-gray-600 hover:text-gray-800"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Detalhes do Usuário</h1>
            <p className="text-gray-600">Visualize as informações completas do usuário</p>
          </div>
        </div>
        
        {hasPermission('usuarios.update') && (
          <Link
            to={`/seguranca/usuarios/${id}/editar`}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPen} />
            Editar
          </Link>
        )}
      </div>

      {/* Informações Básicas */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
            <FontAwesomeIcon icon={faUser} className="text-2xl text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{usuario.nome}</h2>
            <p className="text-gray-600">{usuario.email}</p>
            <div className="flex items-center mt-2">
              <span className={`px-2 py-1 rounded-full text-xs ${
                usuario.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                <FontAwesomeIcon icon={usuario.ativo ? faToggleOn : faToggleOff} className="mr-1" />
                {usuario.ativo ? 'Ativo' : 'Inativo'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* ID */}
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faIdBadge} className="text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-500">ID</p>
              <p className="text-gray-900">{usuario.id}</p>
            </div>
          </div>

          {/* Email */}
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="text-gray-900">{usuario.email}</p>
            </div>
          </div>

          {/* Perfil */}
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faShieldAlt} className="text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-500">Perfil</p>
              <p className="text-gray-900">{usuario.perfil_nome}</p>
              <p className="text-xs text-gray-500">Nível {usuario.nivel_acesso}</p>
            </div>
          </div>

          {/* Email Verificado */}
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-500">Email Verificado</p>
              <span className={`px-2 py-1 rounded-full text-xs ${
                usuario.email_verificado ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {usuario.email_verificado ? 'Verificado' : 'Não Verificado'}
              </span>
            </div>
          </div>

          {/* Último Login */}
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faCalendar} className="text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-500">Último Login</p>
              <p className="text-gray-900">
                {usuario.ultimo_login ? (() => {
                  const date = new Date(usuario.ultimo_login);
                  const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
                  return adjustedDate.toLocaleDateString('pt-BR');
                })() : 'Nunca fez login'}
              </p>
            </div>
          </div>

          {/* Data de Criação */}
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faCalendar} className="text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-500">Criado em</p>
              <p className="text-gray-900">
                {(() => {
                  const date = new Date(usuario.created_at);
                  const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
                  return adjustedDate.toLocaleDateString('pt-BR');
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Permissões */}
      {usuario.permissoes && usuario.permissoes.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            <FontAwesomeIcon icon={faKey} className="mr-2" />
            Permissões ({usuario.permissoes.length})
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {usuario.permissoes.map((permissao, index) => (
              <div key={index} className="border rounded-lg p-3">
                <p className="font-medium text-gray-900">{permissao.nome}</p>
                <p className="text-sm text-gray-600">
                  {permissao.modulo} - {permissao.acao}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estatísticas de Sessão */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Informações de Sessão
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-indigo-600">
              {usuario.tentativas_login || 0}
            </p>
            <p className="text-sm text-gray-600">Tentativas de Login</p>
          </div>
          
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {usuario.ativo ? 'Liberado' : 'Bloqueado'}
            </p>
            <p className="text-sm text-gray-600">Status da Conta</p>
          </div>
          
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">
              {usuario.bloqueado_ate ? 'Sim' : 'Não'}
            </p>
            <p className="text-sm text-gray-600">Bloqueio Temporário</p>
          </div>
        </div>
        
        {usuario.bloqueado_ate && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-red-800">
              <strong>Usuário bloqueado até:</strong> {(() => {
                const date = new Date(usuario.bloqueado_ate);
                const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
                return adjustedDate.toLocaleDateString('pt-BR');
              })()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsuarioView;
